import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePermissions } from '@/features/auth';
import { ApiError } from '@/shared/api/api-error';
import { ConfirmDialog } from '@/shared/components/confirm-dialog';
import { cn } from '@/shared/lib/utils';

import { useResolveComplaint, useSetComplaintStatus } from '../api/complaints.queries';
import type { ComplaintDetails, ComplaintResolution } from '../types/complaint.types';

const REASON_CLASS =
  'border-input bg-background focus-visible:ring-ring w-full rounded-md border px-2.5 py-2 text-xs focus-visible:ring-2 focus-visible:outline-none';

type Pending = 'resolve' | 'reject' | 'reopen' | null;

/**
 * How the complaint ends (complaints.manage), as the page's header actions.
 * Resolving asks for an outcome: no refund closes it now; a refund (also
 * refunds.request) goes to four-eyes approval and closes it when approved.
 */
export function ComplaintActions({ data }: { data: ComplaintDetails }) {
  const complaint = data.complaint;
  const { can } = usePermissions();
  const [pending, setPending] = useState<Pending>(null);

  return (
    <>
      {complaint.status === 'open' ? (
        <>
          <Button size="sm" variant="outline" onClick={() => setPending('reject')}>
            Reject
          </Button>
          <Button
            size="sm"
            onClick={() => setPending('resolve')}
            disabled={data.pendingRefundApprovalId !== null}
          >
            Resolve…
          </Button>
        </>
      ) : (
        <Button size="sm" variant="outline" onClick={() => setPending('reopen')}>
          Reopen
        </Button>
      )}

      {pending === 'resolve' && (
        <ResolveDialog
          complaintId={complaint.id}
          hasOrder={complaint.orderId !== null}
          canRefund={can('refunds.request')}
          canApproveOwn={can('refunds.approve.own')}
          onClose={() => setPending(null)}
        />
      )}
      {(pending === 'reject' || pending === 'reopen') && (
        <StatusDialog
          complaintId={complaint.id}
          action={pending}
          onClose={() => setPending(null)}
        />
      )}
    </>
  );
}

function ResolveDialog({
  complaintId,
  hasOrder,
  canRefund,
  canApproveOwn,
  onClose,
}: {
  complaintId: number;
  hasOrder: boolean;
  canRefund: boolean;
  /** refunds.approve.own: may approve the refund they ask for (Admin). */
  canApproveOwn: boolean;
  onClose: () => void;
}) {
  const [outcome, setOutcome] = useState<ComplaintResolution>('no_refund');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const resolve = useResolveComplaint(complaintId);
  const parsed = Number(amount);
  const refundAllowed = hasOrder && canRefund;
  const valid =
    reason.trim().length >= 3 &&
    (outcome === 'no_refund' || (Number.isFinite(parsed) && parsed > 0));
  const error =
    resolve.error instanceof ApiError
      ? (resolve.error.fieldErrors?.Amount ?? resolve.error.message)
      : null;

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Resolve this complaint"
      description="Choose how it ends. The reason goes in the complaint's History, not to the customer."
      confirmLabel={outcome === 'refund' ? 'Request refund' : 'Resolve'}
      isPending={resolve.isPending}
      confirmDisabled={!valid}
      onConfirm={() =>
        resolve.mutate(
          {
            outcome,
            amount: outcome === 'refund' ? parsed : null,
            reason: reason.trim(),
          },
          { onSuccess: onClose },
        )
      }
    >
      <div className="flex flex-col gap-4 text-xs">
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1.5 font-medium">Outcome</legend>
          <OutcomeOption
            id="outcome-no-refund"
            label="No refund"
            hint="The order was fine, or the problem was fixed another way. Closes now."
            checked={outcome === 'no_refund'}
            onSelect={() => setOutcome('no_refund')}
          />
          <OutcomeOption
            id="outcome-refund"
            label="Refund"
            hint={
              refundAllowed
                ? canApproveOwn
                  ? 'Goes to Approvals; as an Admin you can approve it yourself straight after, on this page. The complaint closes once it is approved.'
                  : 'Another staff member approves it first. The complaint closes when they do.'
                : hasOrder
                  ? 'Needs refund permission: ask someone who can request refunds.'
                  : 'This complaint is not linked to an order, so there is nothing to refund.'
            }
            checked={outcome === 'refund'}
            disabled={!refundAllowed}
            onSelect={() => setOutcome('refund')}
          />
        </fieldset>
        {outcome === 'refund' && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="resolve-amount" className="text-xs">
              Refund amount
            </Label>
            <Input
              id="resolve-amount"
              type="number"
              inputMode="decimal"
              min={0.01}
              step="0.01"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              className="h-8 text-xs"
            />
          </div>
        )}
        <ReasonField
          id="resolve-reason"
          value={reason}
          onChange={setReason}
          placeholder="What did you find, and what did you do?"
        />
        {error && (
          <p role="alert" className="text-destructive">
            {error}
          </p>
        )}
      </div>
    </ConfirmDialog>
  );
}

function OutcomeOption({
  id,
  label,
  hint,
  checked,
  disabled = false,
  onSelect,
}: {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  disabled?: boolean;
  onSelect: () => void;
}) {
  return (
    <div
      className={cn(
        'border-border flex items-start gap-2.5 rounded-md border p-3 transition-colors',
        checked && 'border-primary bg-primary/5',
        disabled && 'opacity-60',
      )}
    >
      <input
        type="radio"
        id={id}
        name="complaint-outcome"
        checked={checked}
        disabled={disabled}
        onChange={onSelect}
        aria-describedby={`${id}-hint`}
        className="accent-primary mt-0.5"
      />
      <div className="flex flex-col gap-0.5">
        <label htmlFor={id} className="font-medium">
          {label}
        </label>
        <p id={`${id}-hint`} className="text-muted-foreground">
          {hint}
        </p>
      </div>
    </div>
  );
}

function StatusDialog({
  complaintId,
  action,
  onClose,
}: {
  complaintId: number;
  action: 'reject' | 'reopen';
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const setStatus = useSetComplaintStatus(complaintId);

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={action === 'reject' ? 'Reject this complaint?' : 'Reopen this complaint?'}
      description={
        action === 'reject'
          ? 'It closes as rejected: nothing was wrong with the order. You can reopen it later.'
          : 'It goes back to open, keeping its conversation and owner.'
      }
      confirmLabel={action === 'reject' ? 'Reject' : 'Reopen'}
      variant={action === 'reject' ? 'destructive' : 'default'}
      isPending={setStatus.isPending}
      confirmDisabled={reason.trim().length < 3}
      onConfirm={() =>
        setStatus.mutate(
          { status: action === 'reject' ? 'rejected' : 'open', reason: reason.trim() },
          { onSuccess: onClose },
        )
      }
    >
      <ReasonField
        id="status-reason"
        value={reason}
        onChange={setReason}
        placeholder={
          action === 'reject' ? 'Why was nothing wrong?' : 'What does the customer still need?'
        }
      />
    </ConfirmDialog>
  );
}

function ReasonField({
  id,
  value,
  onChange,
  placeholder,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id} className="text-xs">
        Reason{' '}
        <span className="text-muted-foreground font-normal">(required, kept in History)</span>
      </Label>
      <textarea
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        rows={3}
        maxLength={500}
        placeholder={placeholder}
        className={REASON_CLASS}
      />
    </div>
  );
}
